<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  ClipboardDocumentCheckIcon,
  Cog8ToothIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline'
import ActionCenterPanel from '@/v2/components/layout/ActionCenterPanel.vue'
import ApyBrand from '@/v2/components/layout/ApyBrand.vue'
import AppLauncher from '@/v2/components/layout/AppLauncher.vue'
import SidebarV2 from '@/v2/components/layout/SidebarV2.vue'
import { findActiveHub, HUB_ACCENTS, sidebarMenu } from '@/v2/data/sidebarMenu'
import DynamicBanners from '@/components/common/layout/DynamicBanners.vue'
import NewsModal from '@/components/common/layout/NewsModal.vue'
import TricksterBanner from '@/components/common/layout/TricksterBanner.vue'
import UserPreferenceModal from '@/components/features/user/UserPreferenceModal.vue'
import Avatar from '@/components/common/ui/Avatar.vue'
import { useOmnisearchStore } from '@/stores/useOmnisearchStore'
import { useSidebarStore } from '@/v2/stores/useSidebarStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import { useActionItemStore } from '@/stores/useActionItemStore'
import { useUserPreferenceStore } from '@/stores/useUserPreferenceStore'
import { useNewsStore } from '@/stores/useNewsStore'
import { storeToRefs } from 'pinia'

const router = useRouter()
const route = useRoute()
const omnisearchStore = useOmnisearchStore()
const sidebarStore = useSidebarStore()
const boostCoreStore = useBoostCorePermissionStore()
const notificationStore = useUserNotificationStore()
const actionStore = useActionItemStore()
const userPreferenceStore = useUserPreferenceStore()
const newsStore = useNewsStore()

const { user } = storeToRefs(boostCoreStore)
const { unreadCount } = storeToRefs(notificationStore)
const { count: actionCount } = storeToRefs(actionStore)
const userEmail = computed(() => user.value?.email ?? undefined)

// Raccourci recherche : ⌘K sur Mac, Ctrl+K ailleurs (le listener App.vue gère déjà les deux)
const isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)

// ── Sous-thème de section ───────────────────────────────────────────────────
// Le hub de niveau 1 contenant la route active définit la couleur de section
// (`--hub-accent`), portée par le badge du header, la barre haute, et réutilisable
// par le contenu (cards en `border-t-2` sur var(--hub-accent)).
const activeHub = computed(() => findActiveHub(sidebarMenu, route.path))
const activeAccent = computed(() => {
  const key = activeHub.value?.accent
  return key ? HUB_ACCENTS[key] : { color: 'var(--apy-s2)', content: 'var(--apy-p1)', label: '' }
})

const userFullName = computed(() => {
  const u = user.value
  if (!u) return ''
  const parts = [u.firstName, u.lastName].filter(Boolean)
  return parts.join(' ') || u.email || ''
})

// ── Verrou de scroll du document (shell V2 uniquement) ──────────────────────
// Le shell V2 gère son propre défilement via <main overflow-y-auto> ; le root
// est `h-dvh overflow-hidden`. On empêche en plus le document (html/body) de
// défiler pour qu'aucune barre parasite n'apparaisse (contenu débordant,
// extension type Google Translate qui gonfle scrollHeight, etc.). En V1
// ApyLayout n'est jamais monté → le scroll document natif y reste intact.
onMounted(() => {
  document.documentElement.classList.add('overflow-hidden')
  document.body.classList.add('overflow-hidden')

  if (boostCoreStore.user) notificationStore.fetchUnreadCount()
  if (boostCoreStore.user) actionStore.fetchCount()
  // Popup "Quoi de neuf ?" s'il y a des news non lues — en V1 c'est la
  // NavBar qui s'en charge ; en mode v2 elle n'est jamais montée.
  newsStore.checkForNews()
})

onUnmounted(() => {
  document.documentElement.classList.remove('overflow-hidden')
  document.body.classList.remove('overflow-hidden')
})
watch(
  () => boostCoreStore.user,
  (u) => {
    if (u) notificationStore.fetchUnreadCount()
    if (u) actionStore.fetchCount()
  },
)

// Le listener Ctrl+K et l'overlay omnisearch sont dans App.vue (globaux)

function logout() {
  router.push('/auth/logout')
}

const openModalPreferences = ref(false)

function openPreferences() {
  userPreferenceStore.fetchPreferences()
  openModalPreferences.value = true
}

/** Ferme le dropdown DaisyUI (piloté par focus-within) avant navigation/action */
function closeDropdown() {
  const el = document.activeElement
  if (el instanceof HTMLElement) el.blur()
}
</script>

<template>
  <div
    class="flex h-dvh flex-col overflow-hidden bg-base-200"
    data-theme="boostsuite-light"
    :style="{ '--hub-accent': activeAccent.color, '--hub-accent-content': activeAccent.content }"
  >
    <!-- Bandeau d'impersonation (« connecté en tant que ») — pleine largeur, au-dessus du shell -->
    <div class="shrink-0">
      <TricksterBanner />
    </div>

    <div class="flex min-h-0 flex-1 overflow-hidden">
      <a
        href="#main"
        class="btn btn-primary sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60]"
      >
        Aller au contenu
      </a>

      <SidebarV2 />

      <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
      <header
        class="flex h-14 shrink-0 items-center gap-2 border-b-[3px] border-base-300 bg-base-100 px-3 lg:px-4"
        :style="{ borderBottomColor: 'var(--hub-accent)' }"
      >
        <!-- Burger + brand : mobile uniquement (le brand desktop vit dans la sidebar) -->
        <button
          type="button"
          class="btn btn-square btn-ghost lg:hidden"
          aria-label="Ouvrir le menu"
          @click="sidebarStore.openMobile()"
        >
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>
        <RouterLink
          to="/accueil"
          class="flex min-h-11 items-center rounded-field focus-visible:outline-2 focus-visible:outline-primary lg:hidden"
          aria-label="APY — Accueil"
        >
          <ApyBrand />
        </RouterLink>

        <!-- Badge de la section active : sous-thème couleur (cf sidebarMenu.accent) -->
        <div v-if="activeHub" class="hidden items-center gap-2 md:flex" aria-hidden="true">
          <span
            class="rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide"
            :style="{
              color: 'color-mix(in oklab, var(--hub-accent) 60%, var(--color-base-content))',
              backgroundColor: 'color-mix(in oklab, var(--hub-accent) 12%, transparent)',
            }"
          >
            {{ activeHub.label }}
          </span>
        </div>

        <!-- Barre de recherche décorative (ouvre l'overlay au clic) -->
        <button
          type="button"
          class="mx-auto hidden max-w-sm flex-1 items-center gap-2 rounded-field bg-base-200 px-3 py-1.5 text-sm text-base-content/70 transition-colors hover:bg-base-300 hover:text-base-content focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none sm:flex"
          @click="omnisearchStore.open()"
        >
          <MagnifyingGlassIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
          <span class="flex-1 text-left">Rechercher…</span>
          <kbd
            class="hidden rounded border border-base-300 bg-base-100 px-1.5 py-0.5 font-mono text-xs lg:inline-block"
          >{{ isMac ? '⌘K' : 'Ctrl+K' }}</kbd>
        </button>

        <div class="ml-auto flex items-center gap-1 sm:ml-0">
          <!-- Recherche (mobile) -->
          <button
            type="button"
            class="btn btn-square btn-ghost sm:hidden"
            aria-label="Rechercher (Ctrl+K)"
            @click="omnisearchStore.open()"
          >
            <MagnifyingGlassIcon class="h-5 w-5" aria-hidden="true" />
          </button>

          <!-- Lanceur d'applications (raccourcis externes) -->
          <AppLauncher />

          <!-- À faire (actions) -->
          <div class="dropdown dropdown-end">
            <button
              type="button"
              tabindex="0"
              class="btn btn-ghost btn-sm relative gap-1.5"
              :aria-label="`À faire (${actionCount.total})`"
            >
              <ClipboardDocumentCheckIcon class="h-5 w-5" aria-hidden="true" />
              <span class="hidden sm:inline">À faire</span>
              <span
                v-if="actionCount.total > 0"
                class="badge badge-sm"
                :class="actionCount.urgent > 0 ? 'badge-error' : 'badge-warning'"
              >
                {{ actionCount.total > 99 ? '99+' : actionCount.total }}
              </span>
            </button>
            <div
              tabindex="0"
              class="dropdown-content z-50 mt-2 w-[26rem] max-w-[calc(100vw-1rem)]"
            >
              <ActionCenterPanel />
            </div>
          </div>

          <!-- Notifications -->
          <RouterLink
            to="/notifications"
            class="btn btn-square btn-ghost relative"
            aria-label="Notifications"
          >
            <BellIcon class="h-5 w-5" aria-hidden="true" />
            <span
              v-if="unreadCount > 0"
              class="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-error text-[10px] text-error-content"
            >
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </RouterLink>

          <!-- Menu utilisateur -->
          <div class="dropdown dropdown-end">
            <button type="button" tabindex="0" class="btn btn-circle btn-ghost" aria-label="Menu utilisateur">
              <Avatar :email="userEmail" size="32" />
            </button>
            <ul
              tabindex="0"
              role="list"
              class="menu dropdown-content z-50 mt-2 w-56 rounded-box border border-base-300 bg-base-100 p-2 shadow-sm"
            >
              <li v-if="userFullName" class="menu-title truncate">{{ userFullName }}</li>
              <li>
                <button
                  type="button"
                  @click="
                    closeDropdown();
                    openPreferences()
                  "
                >
                  <Cog8ToothIcon class="h-4 w-4" aria-hidden="true" />
                  Préférences
                </button>
              </li>
              <li>
                <RouterLink to="/news" @click="closeDropdown()">
                  <SparklesIcon class="h-4 w-4" aria-hidden="true" />
                  Nouveautés
                </RouterLink>
              </li>
              <li>
                <button
                  type="button"
                  class="text-error"
                  @click="
                    closeDropdown();
                    logout()
                  "
                >
                  <ArrowLeftStartOnRectangleIcon class="h-4 w-4" aria-hidden="true" />
                  Déconnexion
                </button>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <!-- Bannières d'info admin (composant V1, conservé en mode mixte) -->
      <div class="shrink-0">
        <DynamicBanners />
      </div>

      <main id="main" tabindex="-1" class="min-h-0 flex-1 overflow-y-auto focus:outline-none">
        <RouterView />
      </main>
      </div>
    </div>

    <UserPreferenceModal
      :show-modal="openModalPreferences"
      @close="openModalPreferences = false"
    />
    <NewsModal />
  </div>
</template>
